import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CellRendererProps,
  Platform,
  SectionList,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import _, { isEmpty } from 'lodash';

import colors from 'config/colors';
import { useAppSelector } from 'store/hooks';
import {
  ItemPreparationStatuses,
  OrderInternalStatuses,
} from 'store/Orders/enums';
import { getOrderInternalStatus } from 'store/Orders/utils';
import { SettingSelectors } from 'store/Settings';
import { Text } from 'components';

import { Item } from './OrderItem';
import { NonCookableActionButton } from './StatusButtons';
import type { Order, OrderItem } from 'store/Orders/types';

type SectionHeaderProps = {
  name: string;
  textSize?: number;
  style?: ViewStyle;
};

function SectionHeader({ name, textSize = 14, style }: SectionHeaderProps) {
  return (
    <View
      style={[
        {
          backgroundColor: '#aca8a7',
          paddingHorizontal: 2,
          paddingVertical: 2,
          justifyContent: 'space-between',
          flexDirection: 'row',
          paddingRight: 12,
        },
        style,
      ]}
    >
      <Text
        color="white"
        font="bold"
        size={textSize}
        style={{ marginHorizontal: 8, textTransform: 'uppercase', flex: 1 }}
      >
        {name}
      </Text>
    </View>
  );
}

function CellRenderer({
  style: styleProp,
  children,
  ...props
}: CellRendererProps<OrderItem>) {
  const { contentContainerStyle, header } = props.item?.options ?? {};

  return (
    <View style={[styleProp, contentContainerStyle]} {...props}>
      {children}
      {/header/gi.test(props.cellKey) && header}
    </View>
  );
}

type Section = {
  name: string;
  nameKey?: string;
  data: OrderItem[];
  options?: Partial<
    SectionHeaderProps & {
      header: ReactNode;
      isLast: boolean;
      textSize: number;
      contentContainerStyle: ViewStyle;
      style: ViewStyle;
    }
  >;
  isLast?: boolean;
};

type OrderItemsProps = {
  items: OrderItem[];
  order: Order;
  disabledScroll?: boolean;
  onStatusChanged?: (props: {
    orderId: string;
    itemId: string;
    mode: 'single' | 'group';
    startDate: Date;
    endDate: Date;
    status: ItemPreparationStatuses;
    screen: string | undefined;
  }) => void;
  onPressItem?: (itemUuid: string) => void;
  shouldPizzaAddonVerticalOnly?: boolean;
  actionButtonArrangement?: 'vertical' | 'horizontal';
  maxNumberOfItemShownInList?: number;
};

export const OrderItems = (props: OrderItemsProps) => {
  const {
    items,
    order,
    disabledScroll,
    shouldPizzaAddonVerticalOnly,
    onStatusChanged,
    onPressItem,
    actionButtonArrangement,
    maxNumberOfItemShownInList,
  } = props;
  const { t } = useTranslation();

  const { shouldDisplayIndividualActionButton } = useAppSelector(
    SettingSelectors.selectActionButtonsInterfaceSettings,
  );

  const orderInternalStatus: OrderInternalStatuses = useMemo(() => {
    return getOrderInternalStatus(order, items);
  }, [order, items]);

  const sections = useMemo(() => {
    const cookablesAndNonCookables = _.groupBy(
      items
        // NOTE: need sort so that the slice would correspond with the groupBy
        .sort((a, b) => {
          if (a.cookable === b.cookable) return 0;
          return a.cookable ? -1 : 1;
        })
        .slice(0, maxNumberOfItemShownInList),
      'cookable',
    );

    const groupedCookablesItems = _.groupBy(
      cookablesAndNonCookables.true,
      'category',
    );
    const groupedNonCookablesItems = _.groupBy(
      cookablesAndNonCookables.false,
      'category',
    );

    const mapFunction = (value: OrderItem[], key: string): Section => ({
      name: key,
      data: value,
    });

    const mappedGroupedNonCookablesItems = _.map(
      groupedNonCookablesItems,
      mapFunction,
    );

    const mappedGroupedCookablesItems = _.map(
      groupedCookablesItems,
      mapFunction,
    );

    const sections: Section[] = [
      ...mappedGroupedCookablesItems,
      ...(!isEmpty(mappedGroupedNonCookablesItems)
        ? [
            {
              name: t('overview.orderDetails.nonCookables'),
              data: [],
              options: {
                contentContainerStyle: styles.nonCookeblesCellContainer,

                header: (
                  <View
                    style={{
                      flexDirection: 'row',
                      backgroundColor: colors.darkGray,
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      padding: 8,
                      gap: 6,
                    }}
                  >
                    <SectionHeader
                      name={t('overview.orderDetails.nonCookables')}
                      // {...section.options}
                      textSize={18}
                      style={[
                        styles.nonCookablesHeader,
                        { flexBasis: 200, maxWidth: '100%' },
                        Platform.OS === 'web' && { wordBreak: 'break-word' },
                      ]}
                    />
                    <NonCookableActionButton
                      order={order}
                      items={items}
                      onStatusChanged={onStatusChanged}
                    />
                  </View>
                ),
              },
            },
            ...mappedGroupedNonCookablesItems,
          ]
        : []),
    ];

    const lastSection = sections.at(-1);

    if (lastSection) {
      sections[sections.length - 1] = { ...lastSection, isLast: true };
    }

    return sections;
  }, [items, onStatusChanged, order, t, maxNumberOfItemShownInList]);

  return (
    <View
      style={{
        overflow: 'hidden',
        backgroundColor: 'white',
        flexShrink: 1,
      }}
    >
      <SectionList
        CellRendererComponent={CellRenderer}
        sections={sections}
        scrollEnabled={!disabledScroll}
        renderItem={({ item, index, section }) => {
          return (
            <>
              {index === 0 && (
                <SectionHeader name={section.name} {...section.options} />
              )}
              <Item
                actionButtonArrangement={actionButtonArrangement}
                order={order}
                shouldDisplayActionButton={shouldDisplayIndividualActionButton}
                item={item}
                index={index}
                shouldDifferentiateLayout={section.data.length > 1}
                orderInternalStatus={orderInternalStatus}
                onStatusChanged={(args) => {
                  if (onStatusChanged) {
                    onStatusChanged(args);
                  }
                }}
                shouldPizzaAddonVerticalOnly={shouldPizzaAddonVerticalOnly}
                onPress={() => {
                  onPressItem?.(item.uuid);
                }}
              />
            </>
          );
        }}
        keyExtractor={(item) => item.uuid}
        // renderSectionHeader={({ section }) => {
        //   if (!section.options?.header) return null;

        //   return <SectionHeader name={section.name} {...section.options} />;
        // }}
        ItemSeparatorComponent={() => (
          <View
            style={{
              borderTopWidth: StyleSheet.hairlineWidth,
              borderColor: '#A3A3A3',
            }}
          />
        )}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  nonCookablesHeader: {
    backgroundColor: colors.darkGray,
    flex: 1,
    zIndex: 10,
    marginHorizontal: -8,
  },
  nonCookeblesCellContainer: { zIndex: 10 },
});
